import { useEffect, useState, memo } from "react";
import { Swords, Shield, Star, Coins, DollarSign, Bookmark, MessageCircle, AlertTriangle, Crown, Handshake, Clock, QrCode, Copy, Loader2, History, Share2, Store } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

import logo from "@/assets/logo.webp";
import ShareMenu from "@/components/ShareMenu";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import { isNotified } from "@/lib/notified-sold";
import { syncFavoritesToServer } from "@/lib/favorites-sync";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { useAnuncioPaymentPolling } from "@/hooks/useAnuncioPaymentPolling";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import OwnerActionButtons from "@/components/OwnerActionButtons";
import SoldRibbon from "@/components/SoldRibbon";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";
import rubiniCoinsIcon from "@/assets/rubini_coins.gif";
import crystalCoinIcon from "@/assets/crystal_coin.gif";

interface MarketplaceCardProps {
  anuncioId: string;
  itemNome: string;
  tipo: "compra" | "venda";
  preco: number;
  moeda: string;
  precoCoins?: number | null;
  precoKk?: number | null;
  servidor: string | null;
  destaque: boolean;
  level?: number | null;
  attack?: number | null;
  defense?: number | null;
  descricao?: string | null;
  createdAt: string;
  expiraEm?: string | null;
  destaqueInicio?: string | null;
  destaqueFim?: string | null;
  updatedAt?: string | null;
  temWhatsapp?: boolean;
  temEmail?: boolean;
  upgradeclass?: number | null;
  imageUrl?: string | null;
  apelido?: string | null;
  isCustom?: boolean;
  isOwner?: boolean;
  anuncioStatus?: string;
  sessionContato?: string;
  sessionToken?: string;
  anuncianteId?: string | null;
  anuncianteAvatarUrl?: string | null;
  preferenciasContato?: string[];
  abacatepayUrl?: string | null;
  valorTaxa?: number | null;
  onClick?: () => void;
  onRequireLogin?: (action: () => void) => void;
  onApelidoClick?: (apelido: string) => void;
  onAnuncioAction?: () => void;
  renovacaoPendente?: boolean;
  taxaPaga?: boolean;
  metodoPagamentoTaxa?: string | null;
  hasActiveIntermediacao?: boolean;
  onIntermediacaoClick?: () => void;
  /** When true, cancel action cancels the whole batch */
  isBatchStacked?: boolean;
  batchCount?: number;
  /** Names of all items in the batch (used to render thumbnails on QR overlay) */
  batchItemNames?: string[];
  /** Full meta of items in the batch (name + destaque flag) for QR overlay distinction */
  batchItemMeta?: { item_nome: string; destaque: boolean }[];
  isInteresseMatch?: boolean;
  isRevendedor?: boolean;
}

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) {
    favs.splice(idx, 1);
    localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
    return false;
  }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

const MarketplaceCard = ({
  anuncioId,
  itemNome,
  tipo,
  preco,
  moeda,
  precoCoins,
  precoKk,
  servidor,
  destaque,
  level,
  attack,
  defense,
  descricao,
  createdAt,
  expiraEm,
  destaqueInicio,
  destaqueFim,
  updatedAt,
  temWhatsapp,
  temEmail,
  upgradeclass,
  imageUrl,
  apelido,
  isCustom,
  isOwner,
  anuncioStatus,
  sessionContato,
  sessionToken,
  anuncianteId,
  anuncianteAvatarUrl,
  preferenciasContato = ["whatsapp"],
  onClick,
  onRequireLogin,
  onApelidoClick,
  onAnuncioAction,
  renovacaoPendente,
  taxaPaga,
  metodoPagamentoTaxa,
  hasActiveIntermediacao,
  onIntermediacaoClick,
  abacatepayUrl,
  valorTaxa,
  isBatchStacked,
  batchCount,
  batchItemNames,
  batchItemMeta,
  isInteresseMatch,
  isRevendedor,
}: MarketplaceCardProps) => {
  const [isFav, setIsFav] = useState(() => getFavorites().includes(anuncioId));
  const [reportOpen, setReportOpen] = useState(false);
  const [intermedioOpen, setIntermedioOpen] = useState(false);
  const [isReported, setIsReported] = useState(() => isNotified(anuncioId));
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [paymentQr, setPaymentQr] = useState<{ base64: string; text: string | null; valorCobrado?: number } | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [anuncioStatus]);

  // Renewal pending = renovacaoPendente flag set by user but renewal not yet paid/approved.
  // For an active/paused/expired ad being renewed (not new ad), status remains the same.
  // If the ad's expira_em already passed without payment, treat the card as encerrado
  // and DO NOT allow paying the renewal anymore.
  const _isDateExpiredEarly = !!(expiraEm && new Date(expiraEm) <= new Date()) && anuncioStatus !== "vendido";
  const isRenewalPending = !!renovacaoPendente && !_isDateExpiredEarly && anuncioStatus !== "aguardando_pagamento" && anuncioStatus !== "encerrado" && anuncioStatus !== "vendido" && anuncioStatus !== "rejeitado" && anuncioStatus !== "expirado" && anuncioStatus !== "pagamento_expirado";
  const isRenewalPaidByCoins = isRenewalPending && metodoPagamentoTaxa === "coins" && taxaPaga === true;
  const isAwaitingPayment = anuncioStatus === "aguardando_pagamento" || isRenewalPending;
  const { confirmed: paymentConfirmed } = useAnuncioPaymentPolling({
    active: !!paymentQr && isAwaitingPayment,
    anuncioId,
    sessionContato,
    sessionToken,
    onConfirmed: () => {
      // Keep QR overlay mounted briefly so the success tick stays visible,
      // then clear and refresh the listing.
      setTimeout(() => {
        setPaymentQr(null);
        onAnuncioAction?.();
      }, 1500);
    },
  });

  const handlePayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loadingPayment) return;
    setLoadingPayment(true);
    try {
      const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
      if (isRenewalPending) {
        const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
          body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "regerar_pagamento" },
        });
        if (!error && data?.pix_qr_code_base64) {
          setPaymentQr({ base64: data.pix_qr_code_base64, text: data.pix_qr_code || null, valorCobrado: data.valor_cobrado });
        } else {
          toast({ title: data?.error || "Erro ao gerar QR Code", variant: "destructive" });
        }
      } else {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { transacao_id: anuncioId, metodo: "pix", contato: sess.contato, token: sess.token },
        });
        if (!error && data?.qr_code_base64) {
          setPaymentQr({ base64: data.qr_code_base64, text: data.qr_code || null, valorCobrado: data.valor_cobrado });
        } else {
          toast({ title: "Erro ao gerar QR Code", variant: "destructive" });
        }
      }
    } catch {
      toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
    }
    setLoadingPayment(false);
  };

  const handleAnuncioAction = () => {
    setDismissed(true);
    setTimeout(() => onAnuncioAction?.(), 400);
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doFav = () => {
      const added = toggleFavorite(anuncioId);
      setIsFav(added);
      syncFavoritesToServer();
      toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
    };
    if (onRequireLogin) {
      onRequireLogin(doFav);
    } else {
      doFav();
    }
  };

  // Removed direct WhatsApp handler - now handled via ContactOptionsPopover + get-contact-info


  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doOpen = () => setReportOpen(true);
    if (onRequireLogin) {
      onRequireLogin(doOpen);
    } else {
      doOpen();
    }
  };

  const formattedDate = (() => {
    try {
      return format(new Date(createdAt), "dd/MM/yy HH:mm", { locale: ptBR });
    } catch {
      return "";
    }
  })();

  const formattedExpireDate = (() => {
    // When destaque is scheduled to start in the future, this card is displayed
    // in the general list as a "mirror" until destaque_inicio. The end date shown
    // should reflect when it leaves this section (destaque_inicio), not the full
    // renewed expira_em.
    let effective = expiraEm;
    if (destaque && destaqueInicio) {
      const di = new Date(destaqueInicio);
      if (!Number.isNaN(di.getTime()) && di.getTime() > Date.now()) {
        effective = destaqueInicio;
      }
    }
    if (!effective) return null;
    try {
      return format(new Date(effective), "dd/MM/yy HH:mm", { locale: ptBR });
    } catch {
      return null;
    }
  })();

  const isPaused = anuncioStatus === "pausado";
  const isDateExpired = !!(expiraEm && new Date(expiraEm) <= new Date()) && anuncioStatus !== "vendido";

  // Destaque→geral downgrade: while the ad is still featured (destaque_fim > now) it stays in the
  // destaque area for everyone. A "ghost" preview of it is mirrored into the general list, visible
  // ONLY to the owner, until destaque_fim — at which point it moves into the general list for all.
  const destaqueFimDate = destaqueFim ? new Date(destaqueFim) : null;
  const isMirrorPreview = !!(
    isOwner &&
    !destaque &&
    destaqueFimDate &&
    !Number.isNaN(destaqueFimDate.getTime()) &&
    destaqueFimDate.getTime() > Date.now()
  );
  const mirrorLabel = isMirrorPreview
    ? destaqueFimDate!.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <>
    <div
      onClick={hasActiveIntermediacao && onIntermediacaoClick ? (e) => { e.stopPropagation(); onIntermediacaoClick(); } : onClick}
      className={`tibia-border bg-card rounded-lg overflow-visible relative cursor-pointer flex flex-col transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] active:scale-[0.98] ${
        dismissed ? "opacity-0 scale-90 translate-y-5" : "opacity-100"
      } ${destaque ? "gold-glow" : ""} ${isInteresseMatch ? "interesse-match-card" : ""}`}
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 320px" }}
    >
      {isInteresseMatch ? (
        <div className="interesse-badge">Interesse</div>
      ) : isRevendedor ? (
        <div className="revendedor-badge" title="Revendedor">Revendedor</div>
      ) : null}
      {/* Mirror preview overlay (destaque→geral downgrade, owner-only) */}
      {isMirrorPreview && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-1.5 px-3 text-center bg-background/85 rounded-lg backdrop-blur-[1px] cursor-default"
        >
          <span className="font-pixel text-[8px] uppercase tracking-wide text-primary-foreground bg-primary/90 px-2 py-1 rounded-sm shadow">
            Pré-visualização
          </span>
          <span className="text-[11px] font-medium text-foreground leading-tight">
            Aparecerá na lista a partir de
          </span>
          <span className="text-xs font-bold text-primary leading-tight">
            {mirrorLabel}
          </span>
          <span className="text-[9px] text-muted-foreground italic">
            Visível apenas para você
          </span>
        </div>
      )}
      {/* Active intermediation overlay */}
      {hasActiveIntermediacao && (
        <div className="absolute inset-0 bg-purple-900/40 z-30 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-purple-500/90 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
            <Handshake className="w-4 h-4 text-white" />
            <span className="font-pixel text-[7px] text-white tracking-wider font-bold">EM INTERMÉDIO</span>
          </div>
        </div>
      )}
      {/* Loading overlay during pause/resume action */}
      {(actionLoading === "pausar" || actionLoading === "retomar") && (
        <div className="absolute inset-0 bg-black/40 z-30 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-card/90 px-3 py-1.5 rounded-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
            <span className="font-pixel text-[8px] text-amber-300 tracking-wider font-bold">
              {actionLoading === "pausar" ? "PAUSANDO..." : "RETOMANDO..."}
            </span>
          </div>
        </div>
      )}
      {/* Paused overlay */}
      {isPaused && !isDateExpired && !isRenewalPending && actionLoading !== "retomar" && (
        <div className="absolute inset-0 bg-black/50 z-30 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-yellow-500/90 px-3 py-1 rounded-sm">
            <span className="font-pixel text-[8px] text-black tracking-wider font-bold">ANÚNCIO PAUSADO</span>
          </div>
        </div>
      )}
      {/* Expired by date overlay */}
      {isDateExpired && anuncioStatus !== "pendente" && anuncioStatus !== "aguardando_pagamento" && (
        <div className="absolute inset-0 bg-black/60 z-30 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-muted/90 px-3 py-1 rounded-sm">
            <span className="font-pixel text-[8px] text-muted-foreground tracking-wider font-bold">ENCERRADO</span>
          </div>
        </div>
      )}
      {/* Pending approval / awaiting payment overlay */}
      {anuncioStatus === "pendente" && (
        <div className="absolute inset-0 bg-amber-900/40 z-30 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-amber-500/90 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-black" />
            <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO APROVAÇÃO</span>
          </div>
        </div>
      )}
      {anuncioStatus === "aguardando_pagamento" && (
        <div className="absolute inset-0 bg-amber-900/40 z-30 rounded-lg flex flex-col items-center justify-center gap-2 pointer-events-none">
          <div className="bg-amber-500/90 px-3 py-1.5 rounded-sm flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-black" />
              <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO PAGAMENTO</span>
            </div>
            {createdAt && (
              <span className="font-pixel text-[7px] text-black/70 tracking-wider">
                até {(() => { try { return format(new Date(new Date(createdAt).getTime() + 24 * 60 * 60 * 1000), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return ""; } })()}
              </span>
            )}
          </div>
          {abacatepayUrl && (
            <button
              onClick={handlePayClick}
              disabled={loadingPayment}
              className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
            >
              {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
              {loadingPayment ? "Gerando..." : "Pagar"}
            </button>
          )}
        </div>
      )}
      {/* Renewal pending overlay (blue) - only visible to owner */}
      {isRenewalPending && isOwner && (
        <div className="absolute inset-0 bg-blue-900/40 z-30 rounded-lg flex flex-col items-center justify-center gap-2 pointer-events-none">
          <div className="bg-blue-500/90 px-3 py-1.5 rounded-sm flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-white" />
              <span className="font-pixel text-[7px] text-white tracking-wider font-bold">RENOVAÇÃO PENDENTE</span>
            </div>
            <span className="font-pixel text-[6px] text-white/80 tracking-wider">
              {isRenewalPaidByCoins ? "Aguardando aprovação" : "Aguardando pagamento"}
            </span>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            {!isRenewalPaidByCoins && (
              <button
                onClick={handlePayClick}
                disabled={loadingPayment || actionLoading === "cancelar_renovacao"}
                className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
              >
                {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                {loadingPayment ? "Gerando..." : "Pagar Renovação"}
              </button>
            )}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (actionLoading) return;
                setActionLoading("cancelar_renovacao");
                try {
                  const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
                  const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
                    body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "cancelar_renovacao" },
                  });
                  if (error || data?.error) {
                    toast({ title: data?.error || "Erro ao cancelar renovação", variant: "destructive" });
                  } else {
                    toast({ title: "Renovação cancelada" });
                    setPaymentQr(null);
                    onAnuncioAction?.();
                  }
                } catch {
                  toast({ title: "Erro ao cancelar renovação", variant: "destructive" });
                } finally {
                  setActionLoading(null);
                }
              }}
              disabled={!!actionLoading}
              className="pointer-events-auto flex items-center gap-1 px-3 py-1 rounded-sm text-[8px] font-pixel bg-destructive/90 text-destructive-foreground border border-destructive/60 hover:bg-destructive transition-colors shadow-md disabled:opacity-50"
            >
              {actionLoading === "cancelar_renovacao" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Cancelar
            </button>
          </div>
        </div>
      )}
      {anuncioStatus === "vendido" && <SoldRibbon />}
      {anuncioStatus === "encerrado" && <SoldRibbon type="cancelado" />}
      {isOwner && (
        <div className="absolute top-1 right-1 z-20 flex items-center gap-0.5 bg-primary/90 text-primary-foreground px-1.5 py-0.5 rounded-sm">
          <Crown className="w-3 h-3" />
          <span className="font-pixel text-[6px] tracking-wider">MEU</span>
        </div>
      )}
      {destaque && (
        <div className="absolute top-1 right-1 z-10">
          <Star className="w-4 h-4 text-primary fill-primary" />
        </div>
      )}
      {isInteresseMatch && isRevendedor && (
        <div className={`revendedor-corner-badge ${isOwner || destaque ? "top-6 right-1" : "top-1 right-1"}`} title="Revendedor">
          Revendedor
        </div>
      )}

      {/* Type header - centered */}
      <div
        className={`px-2 py-1.5 font-pixel text-[7px] tracking-wider flex flex-col items-center gap-0.5 ${
          tipo === "venda"
            ? "bg-red-600/30 text-red-400"
            : "bg-blue-600/30 text-blue-400"
        }`}
      >
        <span className="flex items-center gap-1">
          {tipo === "venda" ? (
            <>
              <DollarSign className="w-3 h-3" />
              VENDA
            </>
          ) : (
            <>
              <Coins className="w-3 h-3" />
              COMPRA
            </>
          )}
        </span>
        <div className="flex flex-col items-center w-full gap-0">
          {servidor && (
            <span className="text-muted-foreground text-[8px] font-bold">🌐 {servidor}</span>
          )}
          {(anuncioStatus === "pendente" || anuncioStatus === "aguardando_pagamento") ? (
            <span className="text-muted-foreground text-[6px]">Criado em: {formattedDate}</span>
          ) : formattedExpireDate ? (
            <span className="text-muted-foreground text-[7px]">Até: {formattedExpireDate}</span>
          ) : (anuncioStatus === "encerrado" && updatedAt) ? (
            <span className="text-muted-foreground text-[7px]">
              Até: {(() => { try { return format(new Date(updatedAt), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return ""; } })()}
            </span>
          ) : (
            // Fallback defensivo: anúncio sem expira_em (estado inconsistente) — mostra data de criação para o card não ficar mudo
            <span className="text-muted-foreground text-[6px]">Criado em: {formattedDate}</span>
          )}
        </div>
      </div>

      {/* Item name centered BEFORE image - fixed height */}
      <div className="px-3 pt-2 h-[22px] flex items-center justify-center">
        <h3 className="font-pixel text-[8px] text-primary leading-tight truncate text-center" title={itemNome}>
          {itemNome}
        </h3>
      </div>

      {/* Image - fixed height */}
      <div className="relative flex flex-col items-center justify-center p-3 bg-secondary/30 h-[100px]">
        {/* History button - top left */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
              className="absolute top-1 left-1 z-10 p-1 rounded-sm bg-transparent hover:scale-110 transition-all"
              title="Histórico de anúncios"
            >
              <History className="w-5 h-5 text-border hover:text-primary" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="z-[100]">Histórico do item</TooltipContent>
        </Tooltip>
        <div className="relative inline-flex items-center justify-center">
        {upgradeclass != null && upgradeclass > 0 && (
          <div className="absolute -top-2 -right-4 z-10">
            <div className="w-5 h-5 rounded-full bg-[#FF8C00] flex items-center justify-center border-2 border-[#FF6B00] shadow-md">
              <span className="font-pixel text-[8px] font-bold text-white">{upgradeclass}</span>
            </div>
          </div>
        )}
        <img
          src={getItemImageUrl(itemNome)}
          alt={itemNome}
          width={62}
          height={62}
          className="w-[62px] h-[62px] object-contain image-rendering-pixelated"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/placeholder.svg";
          }}
          loading="lazy"
          decoding="async"
        />
        </div>
        {/* Stats hidden for cleaner look */}
        {isCustom && (
          <span className="absolute bottom-1 right-1 bg-orange-500/80 text-white font-pixel text-[6px] px-1.5 py-0 rounded-sm z-10">
            CUSTOM
          </span>
        )}
        {apelido && (
          <Badge
            variant="outline"
            title={isRevendedor ? "Revendedor" : undefined}
            className={`absolute bottom-1 left-1 z-10 font-pixel text-[6px] px-2 py-0.5 rounded-sm cursor-pointer transition-colors inline-flex items-center gap-1 ${
              isRevendedor
                ? "bg-indigo-600/90 border-indigo-400 text-white hover:bg-indigo-500 shadow-[0_0_8px_hsl(231_85%_60%/0.6)]"
                : "border-primary/30 bg-black/60 text-primary/90 hover:border-primary hover:text-primary"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onApelidoClick?.(apelido);
            }}
          >
            {isRevendedor && <Store className="w-2 h-2" />}
            {apelido}
          </Badge>
        )}
      </div>

      {/* Info */}
      <div className="px-3 pt-2 pb-3 flex flex-col gap-1">
        {/* Prices - fixed height */}
        {(() => {
          const hasBrl = moeda === "brl" && preco > 0;
          const hasTc = (precoCoins != null && precoCoins > 0) || (moeda === "coins" && preco > 0);
          const hasKk = precoKk != null && precoKk > 0;
          const noPrice = !hasBrl && !hasTc && !hasKk;
          const labelSize = "text-[8px]";
          const valueSize = "text-[9px]";

          return (
            <div className="h-[48px] flex flex-col justify-center space-y-0.5">
              {noPrice ? (
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3 text-blue-400" />
                  <span className="font-pixel text-[8px] text-blue-400">Recebendo propostas...</span>
                </div>
              ) : (
                <>
                  {hasBrl && (
                    <div className="flex items-center gap-1">
                      <span className="font-pixel text-[9px] text-green-400 font-bold shrink-0 drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">R$</span>
                      <span className={`font-pixel ${valueSize} text-primary`}>
                        {preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {hasTc && (
                    <div className="flex items-center gap-1">
                      <img src={rubiniCoinsIcon} alt="RC" className="w-3 h-3" />
                      <span className={`font-pixel ${valueSize} text-primary`}>
                        {(precoCoins || preco).toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                  {hasKk && (
                    <div className="flex items-center gap-1">
                      <img src={crystalCoinIcon} alt="KK" className="w-3 h-3" />
                      <span className={`font-pixel ${valueSize} text-amber-400`}>
                        {precoKk!.toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* Description - fixed 3 lines */}
        <div className="min-h-[36px]">
          <p className="text-[8px] leading-[12px] text-muted-foreground line-clamp-3 break-all" title={descricao || ""}>
            {descricao || "\u00A0"}
          </p>
        </div>
      </div>


      {isOwner ? (
        <div className={`${isPaused || anuncioStatus === "pendente" || isAwaitingPayment ? "relative z-40" : ""} bg-amber-300/20 border-t border-amber-400/30 rounded-b-lg`}>
        <OwnerActionButtons
          anuncioId={anuncioId}
          anuncioStatus={anuncioStatus}
          expiraEm={expiraEm}
          isDestaque={destaque}
          destaqueInicio={destaqueInicio || null}
          sessionContato={sessionContato}
          sessionToken={sessionToken}
          actionLoading={actionLoading}
          setActionLoading={setActionLoading}
          onAnuncioAction={handleAnuncioAction}
          onAnuncioRefresh={onAnuncioAction}
          renovacaoPendente={renovacaoPendente}
          itemNome={itemNome}
          itemImageUrl={imageUrl ? imageUrl : getItemImageUrl(itemNome)}
          isBatchStacked={isBatchStacked}
          batchCount={batchCount}
        />
        </div>
      ) : anuncioStatus === "aguardando_pagamento" || anuncioStatus === "pendente" ? (
        <div className="relative z-40 border-t border-border/40 flex items-center justify-between w-full py-1.5">
          <div className="flex-1 flex justify-center"><div className="p-1 opacity-30 cursor-not-allowed"><MessageCircle className="w-3.5 h-3.5 text-muted-foreground" /></div></div>
          <div className="flex-1 flex justify-center"><div className="p-1 opacity-30 cursor-not-allowed"><Handshake className="w-3.5 h-3.5 text-muted-foreground" /></div></div>
          <div className="flex-1 flex justify-center"><div className="p-1 opacity-30 cursor-not-allowed"><Bookmark className="w-3.5 h-3.5 text-muted-foreground" /></div></div>
          <div className="flex-1 flex justify-center"><div className="p-1 opacity-30 cursor-not-allowed"><Share2 className="w-3.5 h-3.5 text-muted-foreground" /></div></div>
          <div className="flex-1 flex justify-center"><div className="p-1 opacity-30 cursor-not-allowed"><AlertTriangle className="w-3.5 h-3.5 text-muted-foreground" /></div></div>
        </div>
      ) : (
        <div className="relative z-40 border-t border-border/40 flex items-center justify-between w-full py-1.5">
          <div className="flex-1 flex justify-center">
          <ContactOptionsPopover
            anuncioId={anuncioId}
            itemNome={itemNome}
            temWhatsapp={temWhatsapp}
            temEmail={temEmail}
            anuncianteId={anuncianteId}
            anuncianteApelido={apelido}
            anuncianteAvatarUrl={anuncianteAvatarUrl}
            preferenciasContato={preferenciasContato}
            onRequireLogin={onRequireLogin}
            sessionContato={sessionContato}
            sessionToken={sessionToken}
          >
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button className="p-1 hover:scale-110 transition-all">
                  <MessageCircle className="w-3.5 h-3.5 text-green-500/70 hover:text-green-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="z-[100]">Contatar</TooltipContent>
            </Tooltip>
          </ContactOptionsPopover>
          </div>

          <div className="flex-1 flex justify-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button onClick={(e) => { e.stopPropagation(); const doOpen = () => setIntermedioOpen(true); if (onRequireLogin) onRequireLogin(doOpen); else doOpen(); }} disabled={hasActiveIntermediacao} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                <Handshake className="w-3.5 h-3.5 text-purple-400/70 hover:text-purple-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[100]">{hasActiveIntermediacao ? "Em intermédio" : "Solicitar intermédio"}</TooltipContent>
          </Tooltip>
          </div>

          <div className="flex-1 flex justify-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button onClick={handleFavorite} className="p-1 hover:scale-110 transition-all">
                <Bookmark className={`w-3.5 h-3.5 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[100]">Adicionar à lista</TooltipContent>
          </Tooltip>
          </div>

          <div className="flex-1 flex justify-center">
          <ShareMenu
            url={`${window.location.origin}/item/${anuncioId}`}
            text={`Confira "${itemNome}" no RPG Coin!`}
          />
          </div>

          <div className="flex-1 flex justify-center">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button onClick={handleReportClick} disabled={isReported} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400/70 hover:text-red-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[100]">{isReported ? "Já reportado" : "Reportar problema"}</TooltipContent>
          </Tooltip>
          </div>
        </div>
      )}
      
      {/* Inline QR Code payment overlay */}
      {paymentQr && (isAwaitingPayment || paymentConfirmed) && (
        <div
          className="absolute inset-0 bg-card/95 z-40 rounded-lg flex flex-col items-center justify-start gap-1 p-1 pt-2 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {paymentConfirmed ? (
            <div className="flex flex-col items-center gap-2 py-4 animate-fade-in">
              <div className="w-14 h-14 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-pixel text-[10px] text-green-500 font-bold">Pagamento Confirmado!</p>
              <p className="font-pixel text-[7px] text-muted-foreground text-center">Seu anúncio está sendo processado.</p>
            </div>
          ) : (
            <>
              {(() => {
                const meta = batchItemMeta && batchItemMeta.length > 1
                  ? batchItemMeta
                  : (batchItemNames && batchItemNames.length > 1
                      ? batchItemNames.map((n) => ({ item_nome: n, destaque: false }))
                      : null);
                if (!meta) return null;
                const sorted = [...meta].sort((a, b) => Number(b.destaque) - Number(a.destaque));
                const MAX_VISIBLE = 6;
                const visible = sorted.slice(0, MAX_VISIBLE);
                const remaining = sorted.length - visible.length;
                return (
                  <div className="flex flex-wrap items-center justify-center gap-1 mb-2 max-w-full px-1">
                    {visible.map((it, idx) => (
                      <Tooltip key={`${it.item_nome}-${idx}`} delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span className={`relative inline-flex items-center justify-center rounded-sm ${it.destaque ? "ring-2 ring-yellow-400 shadow-[0_0_6px_rgba(250,204,21,0.7)] bg-yellow-400/10" : ""}`}>
                            <img
                              src={getItemImageUrl(it.item_nome)}
                              alt={it.item_nome}
                              width={24}
                              height={24}
                              className="w-6 h-6 object-contain"
                              loading="lazy"
                              decoding="async"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                            />
                            {it.destaque && (
                              <span className="absolute -top-1 -right-1 text-[8px] leading-none">⭐</span>
                            )}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[100]">
                          {it.item_nome}{it.destaque ? " (destaque)" : ""}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    {remaining > 0 && (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span className="min-w-[22px] h-6 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-pixel text-[8px] border border-background shadow">
                            +{remaining}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[100]">+{remaining} itens</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                );
              })() || (

                <div className="flex items-center gap-1.5 mb-2">
                  <img
                    src={imageUrl || getItemImageUrl(itemNome)}
                    alt={itemNome}
                    width={24}
                    height={24}
                    className="w-6 h-6 object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                  />
                  <span className="font-pixel text-[8px] text-foreground leading-none truncate max-w-[140px]">
                    {itemNome}
                  </span>
                </div>
              )}
              <span className="font-pixel text-[10px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)] leading-none">
                R$ {Number(paymentQr.valorCobrado ?? valorTaxa ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
              <span className="font-pixel text-[7px] text-muted-foreground leading-none">Taxa do anúncio</span>
              <div className="relative w-36 h-36">
                <img
                  src={`data:image/png;base64,${paymentQr.base64}`}
                  alt="QR Code PIX"
                  className="w-36 h-36 rounded-sm border border-border"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                    <img src={logo} alt="Logo" className="w-6 h-6 rounded-full object-cover" />
                  </div>
                </div>
              </div>
              {paymentQr.text && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(paymentQr.text!);
                    toast({ title: "Código PIX copiado!" });
                  }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  Copiar código
                </button>
              )}
              <div className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                <span className="text-[7px] text-muted-foreground">Aguardando confirmação...</span>
              </div>
              <button
                onClick={() => setPaymentQr(null)}
                className="text-[7px] font-pixel text-muted-foreground hover:text-foreground transition-colors"
              >
                Fechar
              </button>
            </>
          )}
        </div>
      )}

    </div>

    <ReportProblemDialog
      open={reportOpen}
      onOpenChange={setReportOpen}
      anuncioId={anuncioId}
      itemNome={itemNome}
      tier={upgradeclass}
      servidor={servidor}
      onReported={() => setIsReported(true)}
    />

    {sessionContato && sessionToken && (
      <SolicitarIntermedioDialog
        open={intermedioOpen}
        onOpenChange={setIntermedioOpen}
        anuncioId={anuncioId}
        itemNome={itemNome}
        preco={preco}
        moeda={moeda}
        precoCoins={precoCoins}
        precoKk={precoKk}
        servidor={servidor}
        descricao={descricao}
        anuncianteApelido={apelido}
        anuncianteAvatarUrl={anuncianteAvatarUrl}
        itemImageUrl={imageUrl || getItemImageUrl(itemNome)}
        sessionContato={sessionContato}
        sessionToken={sessionToken}
      />
    )}

    <ItemHistoryDialog
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      itemNome={itemNome}
      sessionContato={sessionContato}
      sessionToken={sessionToken}
      onRequireLogin={onRequireLogin}
      intermediatingAnuncios={hasActiveIntermediacao ? new Set([anuncioId]) : undefined}
    />

    </>
  );
};

export default memo(MarketplaceCard);
